import React, { useEffect } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useParams, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { theme } from './theme/theme';
import Layout from './components/layout/Layout';
import { ROUTES } from './constants';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import WishlistPage from './pages/WishlistPage';
import LoginPage from './pages/LoginPage';
import AccountPage from './pages/AccountPage';
import OrderConfirmationPage from './pages/OrderConfirmationPage';
import OrderDetailsPage from './pages/OrderDetailsPage';
import PaymentStatusPage from './pages/PaymentStatusPage';
import AdminLayout from './components/admin/AdminLayout';
import AdminRoute from './components/admin/AdminRoute';
import AdminProductsPage from './pages/admin/AdminProductsPage';
import AdminProductDetailPage from './pages/admin/AdminProductDetailPage';
import AdminCategoriesPage from './pages/admin/AdminCategoriesPage';
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminOrdersPage from './pages/admin/AdminOrdersPage';
import AdminNavigationPage from './pages/admin/AdminNavigationPage';
import AdminCouponsPage from './pages/admin/AdminCouponsPage';
import AdminStorefrontPage from './pages/admin/AdminStorefrontPage';
import AdminFooterPage from './pages/admin/AdminFooterPage';
import AdminAbandonedCartsPage from './pages/admin/AdminAbandonedCartsPage';
import AdminSettingsPage from './pages/admin/AdminSettingsPage';
import AdminReviewsPage from './pages/admin/AdminReviewsPage';
import AdminPricingCalculatorPage from './pages/admin/AdminPricingCalculatorPage';
import AdminOrderDetailPage from './pages/admin/AdminOrderDetailPage';
import AdminCustomersPage from './pages/admin/AdminCustomersPage';
import { useAppDispatch, useAppSelector } from './hooks';
import { fetchCurrentUser } from './features/authSlice';
import { fetchMyCart, syncGuestCart } from './features/cartSlice';
import { fetchMyWishlist } from './features/wishlistSlice';
import RegisterPage from './pages/RegisterPage';
import NotFoundPage from './pages/NotFoundPage';
import PolicyPage from './pages/PolicyPage';

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const LegacyUnderPriceRedirect: React.FC = () => {
  const { maxPrice } = useParams<{ maxPrice: string }>();
  if (!maxPrice) {
    return <Navigate to={ROUTES.PRODUCTS} replace />;
  }
  return <Navigate to={`${ROUTES.PRODUCTS}?maxPrice=${encodeURIComponent(maxPrice)}`} replace />;
};

function App() {
  const dispatch = useAppDispatch();
  const { token, user } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (token && !user) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch, token, user]);

  useEffect(() => {
    if (token) {
      dispatch(syncGuestCart()).then(() => dispatch(fetchMyCart()));
      dispatch(fetchMyWishlist());
    }
  }, [dispatch, token]);
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <ScrollToTop />
        <Layout>
          <Routes>
            <Route path={ROUTES.HOME} element={<HomePage />} />
            <Route path={ROUTES.PRODUCTS} element={<ProductsPage />} />
            <Route path={`${ROUTES.PRODUCTS}/:id`} element={<ProductDetailPage />} />
            <Route
              path="/under-599"
              element={<Navigate to={`${ROUTES.PRODUCTS}?maxPrice=599`} replace />}
            />
            <Route path="/under-:maxPrice" element={<LegacyUnderPriceRedirect />} />
            <Route path={ROUTES.CART} element={<CartPage />} />
            <Route path={ROUTES.CHECKOUT} element={<CheckoutPage />} />
            <Route path={`${ROUTES.ORDER_CONFIRMATION}/:id`} element={<OrderConfirmationPage />} />
            <Route path={`${ROUTES.PAYMENT_STATUS}/:orderId`} element={<PaymentStatusPage />} />
            <Route path={ROUTES.WISHLIST} element={<WishlistPage />} />
            <Route path={ROUTES.LOGIN} element={<LoginPage />} />
            <Route path={ROUTES.ACCOUNT} element={<AccountPage />} />
            <Route path={`${ROUTES.ACCOUNT}/orders/:id`} element={<OrderDetailsPage />} />
            <Route path={ROUTES.REGISTER} element={<RegisterPage />} />
            <Route path="/:slug" element={<PolicyPage />} />

            <Route
              path={ROUTES.ADMIN}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminDashboardPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/products`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminProductsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/products/:id`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminProductDetailPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/categories`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminCategoriesPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/categories/:id`}
              element={<Navigate to={`${ROUTES.ADMIN}/categories`} replace />}
            />
            <Route
              path={`${ROUTES.ADMIN}/orders`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminOrdersPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/orders/:id`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminOrderDetailPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/pricing-calculator`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminPricingCalculatorPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/reviews`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminReviewsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/settings`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminSettingsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/abandoned-carts`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminAbandonedCartsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/customers`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminCustomersPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/storefront`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminStorefrontPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/footer`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminFooterPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/coupons`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminCouponsPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN}/navigation`}
              element={
                <AdminRoute>
                  <AdminLayout>
                    <AdminNavigationPage />
                  </AdminLayout>
                </AdminRoute>
              }
            />

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Layout>
      </Router>
    </ThemeProvider>
  );
}

export default App;
